// CallPopup -- the 3CX caller card.
//
// Holds one streaming connection to GET /api/calls/stream for the
// logged-in user. When the server's 3CX listener sees an outside call
// ringing on this user's extension it sends a "ring" event carrying the
// caller lookup (customer, sites, customer-since, modules, renewal,
// tickets, last note) and this component pops a card bottom-right.
// "answered" starts the on-call timer; "ended" marks it finished and the
// card clears itself 45s later unless pinned.
//
// Uses fetch() streaming rather than EventSource so the JWT goes in the
// Authorization header, never in a URL that would land in server logs.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import { moduleBadge } from '../lib/moduleBadges.js';

const END_LINGER_MS = 45 * 1000;
const MAX_CARDS = 3;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function recordPath(contact) {
  if (!contact) return null;
  switch (contact.record_type) {
    case 'customer': return `/customers/${contact.id}`;
    case 'lead': return `/leads/${contact.id}`;
    case 'distributor': return `/distributors/${contact.id}`;
    case 'strategic_account': return `/strategic-accounts/${contact.id}`;
    case 'vendor_contact': return `/vendor-contacts/${contact.id}`;
    default: return null;
  }
}

const RECORD_LABEL = {
  customer: 'Customer',
  lead: 'Lead',
  distributor: 'Distributor',
  strategic_account: 'Strategic',
  vendor_contact: 'Vendor'
};

function parseYmd(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function fmtDate(s) {
  const d = parseYmd(s);
  return d ? d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

function customerSince(s) {
  const d = parseYmd(s);
  if (!d) return null;
  const years = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
  const label = d.toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
  if (years < 1) {
    const months = Math.max(1, Math.round(years * 12));
    return `${label} · ${months} mo`;
  }
  return `${label} · ${Math.floor(years)} yr${Math.floor(years) === 1 ? '' : 's'}`;
}

function ago(iso) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function clock(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function daysUntil(ymd) {
  const d = parseYmd(ymd);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (24 * 3600 * 1000));
}

function ModuleBadges({ keys }) {
  if (!keys || !keys.length) return null;
  return (
    <span className="cp-badges">
      {keys.map((k) => {
        const b = moduleBadge(k);
        return (
          <span key={k} className={`badge badge-${b ? b.tone : 'grey'}`} title={k.replace(/_/g, ' ')}>
            {b ? b.abbr : k.slice(0, 3)}
          </span>
        );
      })}
    </span>
  );
}

function StatusLine({ card, now }) {
  const outbound = card.direction === 'outbound';
  if (card.status === 'ringing') {
    return outbound
      ? <span className="cp-status cp-status-ringing"><span className="cp-dot" />Calling…</span>
      : <span className="cp-status cp-status-ringing"><span className="cp-dot" />Incoming call</span>;
  }
  if (card.status === 'connected') {
    return <span className="cp-status cp-status-live">On call {clock(now - card.connectedAt)}</span>;
  }
  if (card.answered) return <span className="cp-status">Call ended</span>;
  if (outbound) return <span className="cp-status">No answer</span>;
  if (card.answeredByExt) return <span className="cp-status">Answered on ext {card.answeredByExt}</span>;
  return <span className="cp-status cp-status-missed">Missed call</span>;
}

// Quick note during / after the call; saved to the record's Notes.
function NoteBox({ contactId, onDraft }) {
  const [text, setText] = useState('');
  const [state, setState] = useState(null); // null | 'saving' | 'saved' | error string

  async function save() {
    if (!text.trim()) return;
    setState('saving');
    try {
      await axios.post('/api/calls/note', { contact_id: contactId, text: text.trim() });
      setText('');
      setState('saved');
      onDraft(false);
    } catch (err) {
      setState(err.response?.data?.error || err.message);
    }
  }

  return (
    <div className="cp-note-box">
      <textarea
        rows={2}
        placeholder="Note about this call…"
        value={text}
        onChange={(e) => { setText(e.target.value); setState(null); onDraft(!!e.target.value.trim()); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) save(); }}
      />
      <div className="cp-note-row">
        {state === 'saved' && <span className="cp-note-ok">Saved to notes</span>}
        {state && state !== 'saved' && state !== 'saving' && <span className="cp-note-err">{state}</span>}
        <button type="button" className="btn-secondary cp-btn" onClick={save} disabled={!text.trim() || state === 'saving'}>
          {state === 'saving' ? 'Saving…' : 'Save note'}
        </button>
      </div>
    </div>
  );
}

// Customer page, Sites tab, with this site expanded (same link the
// Shift Monitor uses).
function sitePath(contactId, site) {
  return `/customers/${contactId}?tab=sites&site_number=${encodeURIComponent(site.number || '')}`;
}

function CallCard({ card, now, onClose, onTogglePin, onOpen, onDraft }) {
  const m = card.match;
  const c = m && m.contact;
  const callingSite = m && m.sites && m.sites.find((s) => s.matched);
  const path = recordPath(c);
  const notifyAvailable = typeof Notification !== 'undefined' && Notification.permission === 'default';
  const renewalDays = m && m.next_renewal ? daysUntil(m.next_renewal.date) : null;

  return (
    <div className={`cp-card cp-card-${card.status}`} role="alert">
      <div className="cp-head">
        <StatusLine card={card} now={now} />
        <span className="cp-ext">Ext {card.ext}</span>
        <button type="button" className={`cp-icon-btn${card.pinned ? ' is-on' : ''}`}
          onClick={onTogglePin} title={card.pinned ? 'Unpin' : 'Keep this card open'}>
          {card.pinned ? 'Pinned' : 'Pin'}
        </button>
        <button type="button" className="cp-icon-btn" onClick={onClose} title="Dismiss">✕</button>
      </div>

      <div className="cp-body">
        <div className="cp-title">{c ? c.display_name : (card.caller_name || 'Unknown caller')}</div>
        <div className="cp-sub">
          {card.number_display}
          {c && c.display_name === c.person && (c.organization || c.company) ? ` · ${c.organization || c.company}` : ''}
          {c && c.person && c.company && c.display_name !== c.person ? ` · ${c.person}` : ''}
          {c && (c.city || c.province) ? ` · ${[c.city, c.province].filter(Boolean).join(', ')}` : ''}
        </div>

        {c && (
          <>
            <div className="cp-tags">
              <span className="badge badge-blue">{RECORD_LABEL[c.record_type] || c.record_type}</span>
              {c.fuel_brand && <span className="badge badge-grey">{c.fuel_brand}</span>}
              {c.pos_type && <span className="badge badge-purple">{c.pos_type}</span>}
              {m.matched_via.type === 'site' && m.matched_via.label && (
                <span className="cp-via">Site line: {m.matched_via.label}</span>
              )}
              {m.matched_via.type === 'sub_contact' && m.matched_via.label && (
                <span className="cp-via">Contact: {m.matched_via.label}</span>
              )}
            </div>

            {(c.record_type === 'strategic_account' || c.record_type === 'vendor_contact' || c.record_type === 'distributor') ? (
              <dl className="cp-facts">
                <dt>Organization</dt><dd>{c.organization || c.company || 'None'}</dd>
                {c.territory && (<><dt>Territory</dt><dd>{c.territory}</dd></>)}
                {c.sites_influenced != null && (<><dt>{c.record_type === 'distributor' ? 'Sites serviced' : 'Sites influenced'}</dt><dd>{c.sites_influenced}</dd></>)}
                {c.account_status && (<><dt>Status</dt><dd>{c.account_status}</dd></>)}
                <dt>Last contact</dt><dd>{c.last_contact ? fmtDate(c.last_contact) : 'None'}</dd>
                <dt>Next contact</dt><dd>{c.next_contact ? fmtDate(c.next_contact) : 'None'}</dd>
              </dl>
            ) : c.record_type === 'lead' ? (
              <dl className="cp-facts">
                <dt>Status</dt><dd>{c.lead_status || 'None'}</dd>
                <dt>Owner</dt><dd>{c.lead_owner || 'Unowned'}</dd>
                <dt>Next contact</dt><dd>{c.lead_next_contact ? fmtDate(c.lead_next_contact) : 'None'}</dd>
              </dl>
            ) : (
              <dl className="cp-facts">
                <dt>Customer since</dt>
                <dd>{customerSince(m.customer_since) || 'No install date'}</dd>
                <dt>Sites</dt>
                <dd>{m.active_site_count} active{m.site_count !== m.active_site_count ? ` of ${m.site_count}` : ''}</dd>
                <dt>Next renewal</dt>
                <dd className={m.next_renewal && (m.next_renewal.overdue || renewalDays <= 30) ? 'cp-warn' : ''}>
                  {m.next_renewal
                    ? `${m.next_renewal.label} · ${fmtDate(m.next_renewal.date)}${m.next_renewal.overdue ? ' (overdue)' : ''}`
                    : 'None'}
                </dd>
                <dt>Open tickets</dt>
                <dd className={m.tickets.open ? 'cp-warn' : ''}>{m.tickets.open}</dd>
              </dl>
            )}

            {m.sites.length > 0 && (
              <ul className="cp-sites">
                {m.sites.map((s) => {
                  const shiftAge = s.last_shift_at ? (Date.now() - new Date(s.last_shift_at).getTime()) / 3600000 : null;
                  return (
                    <li
                      key={s.id}
                      className={`cp-site-link${s.matched ? ' is-matched' : ''}`}
                      role="button"
                      tabIndex={0}
                      title="Open this site"
                      onClick={() => onOpen(sitePath(c.id, s))}
                      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(sitePath(c.id, s)); }}
                    >
                      <div className="cp-site-row">
                        <span className="cp-site-name">
                          {s.name}{s.number && s.name !== s.number ? ` #${s.number}` : ''}
                        </span>
                        {s.status && s.status !== 'Active' && <span className="badge badge-grey">{s.status}</span>}
                      </div>
                      <div className="cp-site-meta">
                        {[s.city, s.province].filter(Boolean).join(', ')}
                        {s.last_shift_at && (
                          <span className={shiftAge > 48 ? 'cp-warn' : ''}> · last shift {ago(s.last_shift_at)}</span>
                        )}
                      </div>
                      <ModuleBadges keys={s.modules} />
                    </li>
                  );
                })}
                {m.site_count > m.sites.length && (
                  <li className="cp-more">+ {m.site_count - m.sites.length} more site(s)</li>
                )}
              </ul>
            )}

            {m.tickets.items.length > 0 && (
              <div className="cp-section">
                <div className="cp-label">Open tickets</div>
                {m.tickets.items.map((t) => (
                  <button key={t.id} type="button" className="cp-link" onClick={() => onOpen(`/tickets/${t.id}`)}>
                    #{t.id} {t.title}
                  </button>
                ))}
              </div>
            )}

            {m.last_note && (
              <div className="cp-section">
                <div className="cp-label">Last note · {fmtDate(m.last_note.date)}</div>
                <div className="cp-note">{m.last_note.text}</div>
              </div>
            )}

            {m.other_match_count > 0 && (
              <div className="cp-hint">This number is also on {m.other_match_count} other record(s).</div>
            )}
          </>
        )}

        {!c && <div className="cp-hint">This number isn't on any record in Command.</div>}
      </div>

      {c && <NoteBox contactId={c.id} onDraft={(v) => onDraft(card.callKey, v)} />}

      <div className="cp-actions">
        {callingSite && c.record_type === 'customer' && (
          <button type="button" className="btn-primary cp-btn" onClick={() => onOpen(sitePath(c.id, callingSite))}>Open site</button>
        )}
        {path && (
          <button type="button" className={`${callingSite && c.record_type === 'customer' ? 'btn-secondary' : 'btn-primary'} cp-btn`} onClick={() => onOpen(path)}>
            Open record
          </button>
        )}
        {!c && <button type="button" className="btn-secondary cp-btn" onClick={() => onOpen('/leads/new')}>New lead</button>}
        {notifyAvailable && (
          <button type="button" className="cp-link cp-notify" onClick={() => Notification.requestPermission()}>
            Turn on desktop alerts
          </button>
        )}
      </div>
    </div>
  );
}

export default function CallPopup() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [now, setNow] = useState(Date.now());
  const cardsRef = useRef(cards);
  cardsRef.current = cards;

  const handleEvent = useCallback((event, data) => {
    if (event === 'presence') {
      window.dispatchEvent(new CustomEvent('xsite-presence', { detail: data }));
      return;
    }
    if (event === 'hello') {
      console.info('[CallPopup] connected', data);
      return;
    }
    if (event === 'ring') {
      setCards((cs) => [
        { ...data, status: 'ringing', receivedAt: Date.now(), pinned: false },
        ...cs.filter((c) => c.callKey !== data.callKey)
      ].slice(0, MAX_CARDS));

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
        const c = data.match && data.match.contact;
        try {
          const n = new Notification(`Incoming call: ${c ? c.display_name : (data.caller_name || data.number_display)}`, {
            body: c ? [data.number_display, c.fuel_brand, `${data.match.active_site_count} site(s)`].filter(Boolean).join(' · ') : data.number_display,
            tag: data.callKey
          });
          n.onclick = () => { window.focus(); n.close(); };
        } catch { /* some browsers block constructor use */ }
      }
      return;
    }
    if (event === 'answered') {
      setCards((cs) => cs.map((c) => (c.callKey === data.callKey ? { ...c, status: 'connected', connectedAt: Date.now() } : c)));
      return;
    }
    if (event === 'ended') {
      setCards((cs) => cs.map((c) => (c.callKey === data.callKey
        ? { ...c, status: 'ended', endedAt: Date.now(), answered: data.answered, answeredByExt: data.answered_by_ext }
        : c)));
    }
  }, []);

  // Stream connection with reconnect.
  useEffect(() => {
    if (!token) return undefined;
    const ctrl = new AbortController();
    let stopped = false;

    (async () => {
      let retry = 0;
      while (!stopped) {
        try {
          const res = await fetch('/api/calls/stream', {
            headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
            cache: 'no-store',
            signal: ctrl.signal
          });
          if (res.status === 401 || res.status === 403) return; // not allowed; don't retry
          if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
          retry = 0;
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buf = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            let i;
            while ((i = buf.indexOf('\n\n')) >= 0) {
              const frame = buf.slice(0, i);
              buf = buf.slice(i + 2);
              let event = 'message';
              let data = '';
              for (const line of frame.split('\n')) {
                if (line.startsWith('event:')) event = line.slice(6).trim();
                else if (line.startsWith('data:')) data += line.slice(5).trim();
              }
              if (data) {
                try { handleEvent(event, JSON.parse(data)); } catch { /* malformed frame */ }
              }
            }
          }
        } catch (err) {
          if (stopped || err.name === 'AbortError') return;
        }
        await sleep(Math.min(30000, 2000 * 2 ** retry++));
      }
    })();

    return () => { stopped = true; ctrl.abort(); };
  }, [token, handleEvent]);

  // Tick for the on-call timer and to clear finished cards.
  useEffect(() => {
    if (!cards.length) return undefined;
    const t = setInterval(() => {
      setNow(Date.now());
      const cutoff = Date.now() - END_LINGER_MS;
      const stale = (c) => c.status === 'ended' && !c.pinned && !c.drafting && c.endedAt < cutoff;
      if (cardsRef.current.some(stale)) {
        setCards((cs) => cs.filter((c) => !stale(c)));
      }
    }, 1000);
    return () => clearInterval(t);
  }, [cards.length]);

  if (!cards.length) return null;

  return (
    <div className="cp-stack">
      {cards.map((card) => (
        <CallCard
          key={card.callKey}
          card={card}
          now={now}
          onClose={() => setCards((cs) => cs.filter((c) => c.callKey !== card.callKey))}
          onTogglePin={() => setCards((cs) => cs.map((c) => (c.callKey === card.callKey ? { ...c, pinned: !c.pinned } : c)))}
          onOpen={(p) => navigate(p)}
          onDraft={(key, v) => setCards((cs) => cs.map((c) => (c.callKey === key ? { ...c, drafting: v } : c)))}
        />
      ))}
    </div>
  );
}
